<template>
  <v-layout>
    <v-main :class="containerState">
      <v-container id="main-container" :fluid="true">
        <!-- Toolbar -->
        <v-app-bar id="upp-bar" color="white" :flat="true" class="px-4">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2" size="large">
              mdi-file-tree
            </v-icon>
            <div class="text-primary text-h5 font-weight-bold">
              {{ $t("labels.mashup", 2) }}
            </div>
          </div>

          <v-spacer></v-spacer>
          <v-row class="d-flex justify-end align-center">
            <v-col
              cols="12"
              sm="12"
              md="10"
              lg="8"
              xl="6"
              class="d-flex align-center align-self-center"
            >
              <v-text-field
                v-model="search"
                class="mr-4 align-self-center"
                density="compact"
                :loading="false"
                variant="solo"
                type="text"
                :label="$t('labels.search')"
                :append-inner-icon="!search ? 'mdi-magnify' : ''"
                :clearable="true"
                single-line
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <!--Button-Dialog-Import-->
          <DialogImport
            :apiUrl="mashupsListConfig.api"
            :tenantId="realm"
            :refreshList="getMashupsList"
            @activateSnackbar="activateSnackbar"
          />

          <!--Button-Dialog-Create-->
          <v-dialog v-model="dialogCreate" max-width="500px">
            <template v-slot:activator="{ props }">
              <v-btn
                variant="elevated"
                color="secondary"
                v-bind="props"
                class="ml-2 text-white"
              >
                {{ $t("labels.create-mashup") }}
              </v-btn>
            </template>
            <v-card>
              <v-toolbar :flat="true" color="secondary">
                <v-toolbar-title class="text-white">
                  {{ $t("labels.create-mashup") }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn color="white" icon @click="closeCreateMashup">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-text>
                <v-form ref="createForm" v-model="validName" lazy-validation>
                  <v-text-field
                    v-model="editedItem.title"
                    :label="$t('labels.name')"
                    :hint="$t('strings.mashup-project-name')"
                    persistent-hint
                    variant="outlined"
                    :rules="nameRules"
                    required
                    type="text"
                    density="default"
                    name="name"
                    hide-details="auto"
                  ></v-text-field>
                </v-form>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary darken-1"
                  variant="text"
                  @click="closeCreateMashup"
                  >{{ $t("labels.cancel") }}</v-btn
                >
                <v-btn
                  color="secondary darken-1"
                  variant="text"
                  :disabled="!validName"
                  @click="createMashupConfirm"
                  >{{ $t("labels.save") }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-app-bar>
        <!-- Table -->
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="mashups"
          :single-select="singleSelect"
          :sort-by="sortBy"
          item-value="title"
          show-select
          :search="search"
          :loading="loader"
          return-object
          hover
        >
          <!-- Actions Toolbar over table -->
          <template v-slot:[`header.title`]>
            <div class="d-inline-flex justify-start align-center">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    variant="plain"
                    color="secondary"
                    v-bind="props"
                    @click="getMashupsList()"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("labels.refresh") }}</span>
              </v-tooltip>

              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon
                    variant="plain"
                    color="secondary"
                    v-bind="props"
                    @click="cycleSort()"
                  >
                    <v-icon>{{ sortIcon }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("labels.sort-" + sortOrder) }}</span>
              </v-tooltip>

              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-btn
                    :disabled="disabled"
                    :class="showDeleteIcon"
                    icon
                    variant="plain"
                    color="secondary"
                    v-bind="props"
                    @click="deleteMultipleMashup()"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span v-if="disabled">{{
                  $t("strings.delete-multiple-mashups-tooltip", selected.length)
                }}</span>
                <span v-else>{{ $t("labels.delete") }}</span>
              </v-tooltip>
            </div>
          </template>
          <!--END Actions Toolbar over table -->

          <!-- NO DATA template -->
          <template v-slot:[`no-data`]>
            <div class="table-no-data">
              <img
                :src="
                  mashupsListConfig.storageImgs + '/commons/watermark-comic.png'
                "
                width="200"
              />
              <p
                class="black--text font-weight-bold text-h6 text-uppercase mb-3"
              >
                {{ $t("strings.no-mashups") }}
              </p>
              <p>{{ $t("strings.please-add-mashups") }}</p>
            </div>
          </template>

          <!-- Table's row definition -->
          <template v-slot:[`item.title`]="{ item }">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-list-item
                  class="px-0 py-2 no-highlight"
                  v-bind="props"
                  @click="routeToMashup(item)"
                >
                  <v-list-item-title
                    class="font-weight-medium"
                    :search="search"
                  >
                    {{ item.title }}
                  </v-list-item-title>
                  <v-list-item-title class="mt-1">
                    <v-chip
                      class="mr-8 text-white"
                      size="x-small"
                      label
                      variant="elevated"
                      :color="
                        loggedUserId === item.owner ? 'primary' : '#3d8ecc'
                      "
                    >
                      <span v-if="loggedUserId === item.owner">
                        {{ $t("labels.owner") }}
                      </span>
                      <span v-else>{{ $t("labels.editor") }}</span>
                    </v-chip>
                    <span class="text-subtitle-2 mr-2">Tags</span>
                    <span class="text-subtitle-2" v-if="item.tags.length === 0">
                      &ndash;
                    </span>
                    <v-chip
                      v-for="(tag, idx) in item.tags"
                      :key="idx"
                      class="mx-1 text-white"
                      variant="elevated"
                      color="secondary"
                      size="x-small"
                    >
                      {{ tag }}
                    </v-chip>
                  </v-list-item-title>
                </v-list-item>
              </template>
              <span>{{ $t("labels.open") }} {{ item.title }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.functions`]="{ item }">
            <v-menu location="bottom left">
              <template v-slot:activator="{ props }">
                <v-btn variant="plain" icon v-bind="props">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list density="default">
                <v-list-item
                  @click.stop="selectMashupVersion('duplicate', item)"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-content-duplicate</v-icon>
                  </template>
                  <v-list-item-title>{{
                    $t("labels.duplicate")
                  }}</v-list-item-title>
                </v-list-item>
                <DialogShare
                  :share-form-config="mashupsListConfig"
                  :realm="realm"
                  :mashup="item"
                ></DialogShare>
                <v-list-item @click="selectMashupVersion('export', item)">
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-download</v-icon>
                  </template>
                  <v-list-item-title>{{
                    $t("labels.export")
                  }}</v-list-item-title>
                </v-list-item>

                <v-tooltip
                  location="left"
                  :disabled="item.owner === loggedUserId"
                >
                  <template v-slot:activator="{ props }">
                    <v-list-item
                      :disabled="item.owner !== loggedUserId"
                      @click.stop="deleteMashup(item)"
                      v-bind="props"
                    >
                      <template v-slot:prepend>
                        <v-icon
                          :disabled="item.owner !== loggedUserId"
                          color="primary"
                          >mdi-delete</v-icon
                        >
                      </template>
                      <v-list-item-title>{{
                        $t("labels.delete")
                      }}</v-list-item-title>
                    </v-list-item>
                  </template>
                  <span>{{ $t("strings.delete-mashup-tooltip") }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </template>
          <!-- END Table's row definition -->
        </v-data-table>
        <!--Snackbar-->
        <v-snackbar
          v-model="snackbar"
          location="top"
          color="green"
          timeout="5000"
        >
          {{ snackbarText }}
          <template v-slot:actions>
            <v-btn color="white" variant="text" @click="snackbar = false"
              >OK</v-btn
            >
          </template>
        </v-snackbar>
        <!--Dialog Delete-->
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-toolbar flat color="secondary">
              <v-toolbar-title class="text-white">
                {{ $t("labels.delete") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon @click="closeDeleteMashup">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <p class="text-h6">
                <span v-if="editedItem.sharedWith.length === 0">
                  {{ $t("strings.delete-mashup-question", numDelete) }}
                </span>
                <span v-else>
                  {{ $t("strings.delete-mashup-shared-question", numDelete) }}
                </span>
              </p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeDeleteMashup"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="deleteConfirm"
              >
                {{ $t("labels.delete") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!--Dialog Duplicate & Export-->
        <v-dialog
          v-model="dialogSelectVersion"
          width=" auto "
          max-width="500px"
          persistent
        >
          <v-card>
            <v-toolbar flat color="secondary">
              <v-toolbar-title class="text-white">
                <span v-if="actionSelectVersion === 'duplicate'">
                  {{ $t("labels.duplicate") }}
                </span>
                <span v-else>
                  {{ $t("labels.export") }}
                </span>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon @click="closeSelectVersion">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text>
              <p class="text-h6 mt-4">
                <span v-if="actionSelectVersion === 'duplicate'">
                  {{ $t("strings.duplicate-mashup-question") }}
                </span>
                <span v-else>
                  {{ $t("strings.export-mashup-question") }}
                </span>
              </p>
              <v-form ref="createForm" v-model="validName" lazy-validation>
                <v-row class="d-flex justify-left">
                  <v-col cols="12" sm="10" class="mb-2">
                    <v-select
                      v-model="selectedVersion"
                      :items="editedItem.versions"
                      item-title="name"
                      item-value="_id"
                      label="Mashup versions"
                      density="default"
                      variant="outlined"
                      return-object
                      hide-details="auto"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" v-if="actionSelectVersion === 'duplicate'">
                    <v-text-field
                      v-model="editedItem.title"
                      :label="$t('labels.name')"
                      :hint="$t('strings.mashup-project-name')"
                      persistent-hint
                      variant="outlined"
                      :rules="nameRules"
                      required
                      density="default"
                      name="name"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeSelectVersion"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="versionMashupConfirm"
              >
                <span v-if="actionSelectVersion === 'duplicate'">
                  {{ $t("labels.duplicate") }}
                </span>
                <span v-else>
                  {{ $t("labels.export") }}
                </span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-overlay :value="overlay" z-index="100">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </v-container>
    </v-main>
  </v-layout>
</template>

<script>
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";
import version from "../version";
import MashupsList from "@/mixins/MashupsList.js";
import DialogShare from "@/components/DialogShare";
import DialogImport from "@/components/DialogImport.vue";
export default {
  name: "MashupsList",
  mixins: [events, utils, MashupsList],
  components: { DialogShare, DialogImport },
  props: ["mashupsListConfig", "realm"],
  data: function () {
    return {
      appVersion: version,
      containerState: "expanded",
      overlay: false,
      loader: false,
      username: "",
      search: "",
      nameRules: [(value) => !!value || this.$t("errors.mashup-name-required")],
      snackbar: false,
      snackbarText: "",
      validName: true,
      dialogDelete: false,
      dialogCreate: false,
      singleSelect: false,
      loggedUserId: "",
      selected: [],
      numDelete: 1,
      showDeleteIcon: "d-none",
      headers: [
        {
          title: "",
          align: "start",
          sortable: false,
          key: "title",
          width: "auto",
        },
        {
          title: "",
          align: "end",
          sortable: false,
          key: "functions",
          width: "5",
        },
      ],
      sortOrder: "desc",
      sortIcon: "mdi-sort-descending",
      sortBy: [{ key: "_id", order: "desc" }],
      mashups: [],
      editedIndex: -1,
      editedItem: {
        _id: "",
        title: "",
        owner: "",
        tags: [],
        latestRelease: "",
        visibility: "",
        sharedWith: [],
        versions: [],
      },
      defaultItem: {
        _id: "",
        title: "",
        owner: "",
        tags: [],
        latestRelease: "",
        visibility: "",
        sharedWith: [],
        versions: [],
      },
    };
  },

  mounted() {
    console.log("Mounted demf-mashups-list v", this.appVersion);
    this.loggedUserId = localStorage.getItem("loggedUserId");

    document.addEventListener("language-localstorage-changed", (event) => {
      this.setLocale(event.detail.storage);
    });

    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener("sidenav-localstorage-changed", (event) => {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    });
  },
  computed: {
    disabled() {
      return this.selected.some((e) => e.owner !== this.loggedUserId);
    },
  },
  watch: {
    selected() {
      this.showDeleteIcon = this.selected.length > 0 ? "d-block" : "d-none";
    },
  },
  methods: {
    cycleSort() {
      if (this.sortOrder === "desc") {
        this.sortOrder = "asc";
        this.sortIcon = "mdi-sort-ascending";
        this.sortBy = [{ key: "_id", order: "asc" }];
      } else {
        this.sortOrder = "desc";
        this.sortIcon = "mdi-sort-descending";
        this.sortBy = [{ key: "_id", order: "desc" }];
      }
    },
  },
};
</script>
<style scoped>
.table-no-data {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

main.expanded {
  margin-left: 56px;
}

main.expanded header#upp-bar {
  margin-left: 56px;
  width: calc(100% - 56px) !important;
}

main.reduced {
  margin-left: 256px;
}

main.reduced header#upp-bar {
  margin-left: 256px;
  width: calc(100% - 256px) !important;
}

iframe {
  border: 0;
}

#main-container {
  height: calc(100vh - 104px);
  overflow-y: auto;
  background-color: white;
}

.no-highlight :deep(.v-list-item__overlay) {
  background: transparent !important;
}
</style>
